/**
 * The policy library frame: a working list on the left, the policy on the right.
 *
 * One policy inside a frame that keeps the list — not several documents at
 * once, see policyLayout.tsx — and the reason for it is the one a user gave:
 * after loading a list of sixty overdue policies, opening one should not mean
 * losing the sixty.
 *
 * WHY A PATHLESS LAYOUT. The two routes involved are siblings, not parent and
 * child: `/policies` and `/policy/:policyId`. Nesting the detail under the list
 * would have meant `/policies/:policyId`, thirteen link sites updated and a
 * pair of redirects for anything already pointing at the old shape. A layout
 * route with no path of its own wraps both instead, so the URLs are untouched
 * and a policy link keeps working exactly as it did.
 *
 * THE LIST PANE APPEARS ONLY WHEN THE POLICY WAS OPENED FROM THE LIST. A link
 * from the library's table carries where in the list it was (which page of
 * which view) in the browser's history entry. Arrive at a policy from an
 * email or a bookmark and there is no such place, so the policy gets the whole
 * width rather than sitting beside a list it did not come from. Arrive from
 * the list, having chosen "Keep the list", and the page you were on stays
 * beside it: the same question to the server, the same page of the answer.
 *
 * The document viewer is deliberately NOT inside this frame — it is its own
 * top-level route. A portrait letter-size PDF in a half-width pane means either
 * horizontal scrolling or type too small to read, which is the problem this
 * application exists to solve rather than one to introduce.
 */

import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { usePolicyLayout } from './policyLayout';
import { PoliciesPage } from './library/PoliciesPage';
import { PolicyListPane } from './PolicyListPane';
import type { LibraryLinkState } from './library/useLibrary';
import styles from './PolicyLibrary.module.css';

/** The route this frame treats as "a policy is open". */
export const DETAIL_PATH = '/policy/:policyId';

export function PolicyLibrary() {
  const detail = useMatch(DETAIL_PATH);
  const location = useLocation();
  const [layout, setLayout] = usePolicyLayout();

  const selectedId = detail?.params.policyId ?? null;

  // The library itself. The table is the page; there is no frame to raise
  // around it until a policy is opened out of it.
  if (selectedId === null) return <PoliciesPage />;

  // Whether this policy was opened from the list, which is what gives the
  // rail a list to show. Checked for shape: history state is input.
  const lib = (location.state as Partial<LibraryLinkState> | null)?.lib;
  const cameFromList = Boolean(lib && typeof lib.key === 'string' && Number.isInteger(lib.page));

  // The user's choice comes first, and "One at a time" is the default: a
  // policy gets the whole width unless somebody asked to keep the list.
  if (layout !== 'split' || !cameFromList) return <Outlet />;

  return (
    <div className={styles.split}>
      {/* Hidden below 900px by CSS rather than unmounted; the policy carries a back arrow. */}
      <aside className={styles.listPane} aria-label="The list this policy was opened from">
        <PolicyListPane selectedId={selectedId} layout={layout} onLayoutChange={setLayout} />
      </aside>
      <div className={styles.detailPane}>
        <Outlet />
      </div>
    </div>
  );
}

/**
 * What `/policies` renders into the frame's outlet: nothing.
 *
 * The list is the frame's own furniture, not its content, so the index route
 * contributes no element. It exists so the path is matched at all.
 */
export function PolicyLibraryIndex() {
  return null;
}
